import React, { useState, useRef, useEffect, ReactElement } from "react";
import Button from "../button";
import classNames from "classnames";
import uniqueId from "lodash.uniqueid";
import { ExpandCollapseProps, ToggleMethodType } from "./expand-collapse.types";
import {
  showSSRHydrationWarning,
  isPropEmpty,
} from "../utils/SSR/showSSRHydrationWarning";
import { useSSR } from "../utils/SSR/useSSR";

export const ExpandCollapse = ({
  id,
  showTrigger = "View More",
  hideTrigger = "View Less",
  children,
  expanded: expandedProp,
  defaultExpanded = false,
  onClick,
  onBeforeExpand,
  onAfterExpand,
  onBeforeCollapse,
  onAfterCollapse,
}: ExpandCollapseProps): ReactElement => {
  const { current: uniquePanelContentId } = useRef(uniqueId("panel__content"));
  useSSR(
    () => isPropEmpty(id) && showSSRHydrationWarning("id", "ExpandCollapse")
  );
  const ariaControlsId = id || uniquePanelContentId;
  const minHeightClass = `m-h_${ariaControlsId}`;
  const fullHeightClass = `f-h_${ariaControlsId}`;

  const expandCollapseContainerRef = useRef<HTMLDivElement>(null);
  const [expandedState, setExpandedState] = useState<boolean>(
    expandedProp || defaultExpanded
  );

  const { current: isControlled } = useRef(expandedProp !== undefined);

  const expanded = isControlled ? expandedProp : expandedState;

  const isFirstRender = useRef<boolean>(true);

  if (expandedProp !== undefined && typeof onClick !== "function") {
    console.error(
      "You are using the 'expanded' prop without supplying an 'onClick' function on <ExpandCollapse>.\n" +
        "Alternatively, rename your 'expanded' prop to 'defaultExpanded' if you don't need a controlled component."
    );
  }

  useEffect(() => {
    const head = document.head || document.getElementsByTagName("head")[0];
    const styleEl = document.createElement("style");
    const css = `.${minHeightClass} { height: 0px; overflow:hidden; } `;

    styleEl.appendChild(document.createTextNode(css));
    head.appendChild(styleEl);

    styleEl.id = `style_id_${ariaControlsId}`;

    const updateEmbeddedCSS = (newHeight: number | undefined): void => {
      const css = `
        .${fullHeightClass} { height: ${newHeight}px;}
        .${minHeightClass} { height: 0px; overflow:hidden; }
      `;
      styleEl.appendChild(document.createTextNode(css));
    };

    const updateStylesTimeout = setTimeout(() => {
      const height = document.getElementById(ariaControlsId)?.scrollHeight;
      updateEmbeddedCSS(height);
    }, 400);

    const handleElementResize = (): void => {
      styleEl.innerHTML = "";
      const height = document.getElementById(ariaControlsId)?.scrollHeight;
      updateEmbeddedCSS(height);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleElementResize);
    }

    return () => {
      clearTimeout(updateStylesTimeout);
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleElementResize);
      }
    };
  }, [ariaControlsId, minHeightClass, fullHeightClass]);

  useEffect(() => {
    const containerParent = expandCollapseContainerRef.current?.parentElement;
    const collapse = (): void => {
      containerParent?.classList.remove("expanded");
    };
    const expand = (): void => {
      containerParent?.classList.add("expanded");
    };

    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (expanded) {
        expand();
      }
    } else {
      if (expanded) {
        onBeforeExpand?.();
        expand();
        onAfterExpand?.();
      } else {
        onBeforeCollapse?.();
        collapse();
        onAfterCollapse?.();
      }
    }
  }, [
    expanded,
    onAfterCollapse,
    onAfterExpand,
    onBeforeCollapse,
    onBeforeExpand,
  ]);

  const _onToggle = (e: ToggleMethodType): void => {
    const flippedExpandStatus = !expanded;
    onClick?.(e, flippedExpandStatus);
    setExpandedState(flippedExpandStatus);
  };

  const triggerProps = {
    "aria-controls": ariaControlsId,
    onClick: _onToggle,
  };

  const expandClasses = classNames(
    "spark-animate-height",
    expanded ? fullHeightClass : minHeightClass
  );

  const showCaret =
    typeof showTrigger === "string" && typeof hideTrigger === "string";

  return (
    <div className="spark-expand">
      <div
        id={ariaControlsId}
        className={expandClasses}
        aria-hidden={expanded}
        ref={expandCollapseContainerRef}
      >
        {children}
      </div>

      <Button
        {...triggerProps}
        id={`${ariaControlsId}_show_button`}
        className="spark-expand__hide--expanded spark-btn spark-btn--text spark-expand__toggle"
        aria-expanded="false"
        aria-hidden={!!expanded}
        textOnly
      >
        {showTrigger}
        {showCaret && <span className="spark-expand__toggle-caret" />}
      </Button>

      <Button
        {...triggerProps}
        id={`${ariaControlsId}_hide_button`}
        className="spark-expand__show--expanded spark-btn spark-btn--text spark-expand__toggle spark-mar-t-2"
        aria-expanded="true"
        aria-hidden={!expanded}
        textOnly
      >
        {hideTrigger}
        {showCaret && <span className="spark-expand__toggle-caret" />}
      </Button>
    </div>
  );
};

ExpandCollapse.displayName = "ExpandCollapse";
